/* eslint-disable react-hooks/exhaustive-deps */
import styles from 'styles/components/onboarding.module.scss';
import { useContext, useEffect, useState } from 'react';
import Welcome from './welcome';
import EnterDomain from './enterDomain';
import DomainContext from 'services/domainContext';
import Loader from 'components/loader';
import VerifyLinkedin from './verifyLinkedin';
import RejectedValidation from './rejected';
import ChoosePlan from './choosePlan';
import ProfileContext from 'services/profileContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, message } from 'antd';
import Logo from 'components/logo';
import { logout } from 'services/api/users';
import { CloseOutlined } from '@ant-design/icons';
import posthog from 'posthog-js';
import {
  PROFILE_VALIDATION_STATUS,
  getProfileValidationStatus,
  isPlanStepDone,
} from 'utils/profileValidation';

const STEPS = {
  WELCOME: 'WELCOME',
  ENTER_DOMAIN: 'ENTER_DOMAIN',
  LINKEDIN: 'LINKEDIN',
  REJECTED: 'REJECTED',
  CHOOSE_PLAN: 'CHOOSE_PLAN',
};

// Pick a plan, LinkedIn, then straight into the app (verification is an
// optional dashboard banner).
const STEP_ORDER = [
  STEPS.WELCOME,
  STEPS.ENTER_DOMAIN,
  STEPS.CHOOSE_PLAN,
  STEPS.LINKEDIN,
];


const Onboarding = ({ firstOnboarding = false, close = () => {} }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.WELCOME);
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const { domains } = useContext(DomainContext);
  const { profile, clearProfile } = useContext(ProfileContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const comingFromLinkedinConnect = searchParams.get('linkedin_connect');
  const onboardingParam = searchParams.get('onboarding');
  const hasDomain = domains?.length > 0;

  const alreadyHasOneDomainApproved =
    domains?.filter((e) => e.status === 'APPROVED').length > 0;

  const canDismissOnboarding = alreadyHasOneDomainApproved;

  const goToPostSetupStep = () => {
    const status = getProfileValidationStatus(profile, domains);
    if (
      status === PROFILE_VALIDATION_STATUS.REJECTED &&
      !alreadyHasOneDomainApproved
    ) {
      setCurrentStep(STEPS.REJECTED);
      return;
    }
    close();
  };

  useEffect(() => {
    // initial load, determine which step suits best
    //only redirect if this is the FIRST Onboarding
    if (!firstLoadDone) {
      setFirstLoadDone(true);
      // If coming back from linkedin connect, go to linkedin step
      // https://app.karmalinks.io/#/dashboard?linkedin_connect=true
      if (onboardingParam) {
        // Back from Stripe checkout started on "Choose your plan".
        setCurrentStep(
          onboardingParam === 'plan' ? STEPS.CHOOSE_PLAN : STEPS.LINKEDIN
        );
        setSearchParams((prev) => {
          prev.delete('onboarding');
          return prev;
        });
      } else if (
        comingFromLinkedinConnect &&
        comingFromLinkedinConnect === 'true'
      ) {
        setCurrentStep(STEPS.LINKEDIN);
        setSearchParams((prev) => {
          prev.delete('linkedin_connect');
          return prev;
        });
      } else if (
        comingFromLinkedinConnect &&
        comingFromLinkedinConnect === 'error'
      ) {
        setCurrentStep(STEPS.LINKEDIN);
        posthog.capture('linkedin_connect_failed', {
          reason: searchParams.get('error') || 'unknown',
        });
        message.error(
          searchParams.get('error') === 'already_used'
            ? 'This LinkedIn profile is already linked to another account.'
            : 'Linkedin connection failed, please try again.'
        );
      } else {
        if (!firstOnboarding) {
          setCurrentStep(STEPS.ENTER_DOMAIN);
        } else if (!hasDomain) {
          setCurrentStep(STEPS.WELCOME);
        } else if (!isPlanStepDone(profile)) {
          setCurrentStep(STEPS.CHOOSE_PLAN);
        } else if (!profile.linkedinProfile) {
          setCurrentStep(STEPS.LINKEDIN);
        } else {
          goToPostSetupStep();
        }
      }
    }
  }, []);

  // The onboarding is a modal on /dashboard, so pageviews can't tell the
  // steps apart.
  useEffect(() => {
    if (!firstLoadDone) return;
    posthog.capture('onboarding_step_viewed', {
      step: currentStep.toLowerCase(),
      first_onboarding: firstOnboarding,
    });
  }, [currentStep, firstLoadDone]);

  useEffect(() => {
    if (!firstLoadDone) return;

    const status = getProfileValidationStatus(profile, domains);
    if (
      hasDomain &&
      !!profile.linkedinProfile &&
      status === PROFILE_VALIDATION_STATUS.REJECTED &&
      !alreadyHasOneDomainApproved &&
      currentStep !== STEPS.REJECTED
    ) {
      setCurrentStep(STEPS.REJECTED);
    }
  }, [profile, domains, currentStep, firstLoadDone]);

  const nextStep = () => {
    if (currentStep === STEPS.REJECTED) {
      return;
    }
    if (currentStep === STEPS.LINKEDIN) {
      goToPostSetupStep();
      return;
    }
    let next = STEP_ORDER[STEP_ORDER.indexOf(currentStep) + 1];
    if (next === STEPS.CHOOSE_PLAN && isPlanStepDone(profile)) {
      next = STEPS.LINKEDIN;
    }
    if (next === STEPS.LINKEDIN && profile.linkedinProfile) {
      goToPostSetupStep();
      return;
    }
    if (!next) {
      goToPostSetupStep();
      return;
    }
    setCurrentStep(next);
  };

  const previousStep = () => {
    const index = STEP_ORDER.indexOf(currentStep);
    if (index <= 0) {
      return;
    }
    setCurrentStep(STEP_ORDER[index - 1]);
  };

  const getStepContent = () => {
    switch (currentStep) {
      default:
      case STEPS.WELCOME:
        return <Welcome nextStep={nextStep} />;
      case STEPS.ENTER_DOMAIN:
        return <EnterDomain nextStep={nextStep} />;
      case STEPS.LINKEDIN:
        return (
          <VerifyLinkedin nextStep={nextStep} previousStep={previousStep} />
        );
      case STEPS.REJECTED:
        return <RejectedValidation />;
      case STEPS.CHOOSE_PLAN:
        return <ChoosePlan nextStep={nextStep} />;
    }
  };
  const navigate = useNavigate();
  return (
    <div className={styles.wrapper}>
      <div className={styles.inner}>
        <div className={styles.content}>
          <div className={styles.vignetteWrapper}>
            {firstLoadDone ? (
              <div className={styles.vignetteInner}>
                <div className={styles.logoWrapper}>
                  <div className={styles.logoInner}>
                    <Logo />
                  </div>
                  <div className={styles.ctaHeader}>
                    {canDismissOnboarding ? (
                      <Button
                        type="link"
                        className={styles.logout}
                        icon={<CloseOutlined />}
                        onClick={() => {
                          close();
                        }}
                      >
                        Close
                      </Button>
                    ) : (
                      <Button
                        type="link"
                        className={styles.logout}
                        onClick={() => {
                          logout(() => {
                            navigate('/login');
                            clearProfile();
                          });
                        }}
                      >
                        Logout
                      </Button>
                    )}
                  </div>
                </div>
                {getStepContent()}
              </div>
            ) : (
              <div className={styles.loader}>
                <Loader />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
